import React, { useEffect, useRef, useState } from 'react';
import { Check, Share2 } from 'lucide-react';
import { createShareLink, buildSharedReportUrl } from '../../../lib/quoteCheck';

// Share card (PRD §5.9, K-factor wave): turn the live report into a
// tokenized public link the visitor can text to a spouse / drop in a group
// chat. The share moment sits right after "what this quote gets right" —
// the delight peak.
//
// COUNSEL CONSTRAINT (do not weaken): the SHARED view is redacted
// SERVER-SIDE — contractor de-identified, exact address removed. This card
// must say so ("contractor name is hidden on the shared view") so the
// uploader knows what they're publishing; only their own session ever shows
// the name.
//
// Clipboard reality on phones (~90% of traffic): navigator.clipboard can be
// unavailable or permission-blocked (iOS Safari outside a user gesture), so
// after minting we ALWAYS render the URL in a readonly input as the manual
// fallback — copy success only upgrades the message.

const COPIED_MSG = 'Link copied — contractor name is hidden on the shared view.';
const MANUAL_COPY_MSG =
  'Copy the link below — contractor name is hidden on the shared view.';

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    /* fall through to the manual-copy fallback */
  }
  return false;
}

const ShareCard = ({ runId, city, pushDataLayer }) => {
  const [shareUrl, setShareUrl] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'working' | 'ready'
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const urlInputRef = useRef(null);

  // A fresh run (re-analysis) mints its own link — never reuse the old one.
  useEffect(() => {
    setShareUrl('');
    setStatus('idle');
    setMessage('');
    setError('');
  }, [runId]);

  const share = async () => {
    if (!runId || status === 'working') return;
    setError('');

    // Re-click with the link already minted: just copy again.
    if (shareUrl) {
      const copied = await copyToClipboard(shareUrl);
      setMessage(copied ? COPIED_MSG : MANUAL_COPY_MSG);
      return;
    }

    setStatus('working');
    try {
      const { shareToken } = await createShareLink(runId);
      const url = buildSharedReportUrl(shareToken);
      setShareUrl(url);
      setStatus('ready');
      // GTM: event + city only — NEVER the token (a public-report key does
      // not belong in the analytics stream).
      pushDataLayer?.('quote_check_share_created', { city: city || null });
      const copied = await copyToClipboard(url);
      setMessage(copied ? COPIED_MSG : MANUAL_COPY_MSG);
    } catch (err) {
      setStatus('idle');
      setError(err?.message || "We couldn't create the share link just now. Please try again.");
    }
  };

  const selectUrl = () => {
    urlInputRef.current?.select?.();
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
      <div className="flex items-start gap-3.5">
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-[#0a6eb8]"
          aria-hidden="true"
        >
          <Share2 size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[17px] font-extrabold tracking-tight text-[#0a1628]">
            Share this report
          </h3>
          <p className="mt-1 text-[13.5px] leading-relaxed text-slate-600">
            Send the checkup to a spouse, neighbor, or group chat. Anyone with
            the link sees this report with the contractor's name and your exact
            address removed.
          </p>

          <button
            type="button"
            onClick={share}
            disabled={status === 'working'}
            className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-[#0a1628] px-6 py-3 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-[#0a6eb8] disabled:opacity-60 sm:w-auto"
          >
            {status === 'working' ? (
              'Creating your link…'
            ) : (
              <>
                <Share2 size={17} aria-hidden="true" />
                {shareUrl ? 'Copy the link again' : 'Share this report'}
              </>
            )}
          </button>

          {shareUrl && (
            <div className="mt-3">
              <input
                ref={urlInputRef}
                type="text"
                readOnly
                value={shareUrl}
                onFocus={selectUrl}
                onClick={selectUrl}
                aria-label="Shareable report link"
                className="min-h-[44px] w-full rounded-xl border border-slate-300 bg-[#f7f9fc] px-3.5 text-[13px] text-[#0a1628] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
              />
              {message && (
                <p
                  className="mt-2 flex items-start gap-1.5 text-[12.5px] font-semibold text-emerald-700"
                  role="status"
                  aria-live="polite"
                >
                  <Check size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{message}</span>
                </p>
              )}
            </div>
          )}

          {error && (
            <p className="mt-2.5 text-[12.5px] leading-relaxed text-red-700" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default ShareCard;
